/**
 * Minimal i18n runtime.
 *
 * Two languages, no lazy namespaces and no ICU messages beyond simple counts,
 * so a library would be ~40 KB for features this app never uses. Intl.PluralRules
 * does the only genuinely hard part — Russian one/few/many — for free.
 */

import type { Lang } from '@/domain/content.types';
import { en, type TableKey, type TKey } from './en';
import { ru } from './ru';

const TABLES: Record<Lang, Record<TableKey, string>> = { en, ru };

export type TParams = Record<string, string | number>;

const pluralRules = new Map<Lang, Intl.PluralRules>();

function pluralCategory(lang: Lang, n: number): Intl.LDMLPluralRule {
  let rules = pluralRules.get(lang);
  if (!rules) {
    rules = new Intl.PluralRules(lang);
    pluralRules.set(lang, rules);
  }
  return rules.select(n);
}

function interpolate(template: string, params?: TParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => {
    const value = params[key];
    return value === undefined ? whole : String(value);
  });
}

/**
 * Look up `key`, resolving a plural variant when `params.n` is a number.
 * Falls back through: exact plural -> `#other` -> bare key -> English -> key.
 */
export function translate(lang: Lang, key: TKey, params?: TParams): string {
  const table = TABLES[lang];
  const n = params?.['n'];

  if (typeof n === 'number') {
    const category = pluralCategory(lang, n);
    const exact = table[`${key}#${category}` as TableKey];
    if (exact !== undefined) return interpolate(exact, params);
    const other = table[`${key}#other` as TableKey];
    if (other !== undefined) return interpolate(other, params);
  }

  const direct = table[key as TableKey];
  if (direct !== undefined) return interpolate(direct, params);

  const fallback = en[key as TableKey];
  return fallback === undefined ? key : interpolate(fallback, params);
}

/** Detect an initial language: stored value wins, then browser, then English. */
export function detectLang(stored?: string | null): Lang {
  if (stored === 'en' || stored === 'ru') return stored;
  const candidates =
    typeof navigator !== 'undefined'
      ? (navigator.languages ?? [navigator.language]).filter(Boolean)
      : [];
  for (const tag of candidates) {
    if (typeof tag === 'string' && tag.toLowerCase().startsWith('ru')) return 'ru';
  }
  return 'en';
}

export type { TKey };
